import * as React from "react";
import {
  useFormContext,
  Controller,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Label } from "../../../../components/ui/label";
import { Input } from "../../../../components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../../components/ui/select";
import { useEffect } from "react";

const TRAILER_TYPES = [
  { value: "dry_freight", label: "Dry Freight" },
  { value: "flatbed", label: "Flatbed" },
  { value: "refer", label: "Refer" }, // as requested (spelling preserved)
  { value: "gooseneck", label: "Gooseneck" },
];

const COVERAGE_AMOUNTS = [
  {
    value: "15000",
    label: "$ 15,000",
  },
  {
    value: "20000",
    label: "$ 20,000",
  },
  {
    value: "30000",
    label: "$ 30,000",
  },
  {
    value: "40000",
    label: "$ 40,000",
  },
  {
    value: "50000",
    label: "$ 50,000",
  },
  {
    value: "60000",
    label: "$ 60,000",
  },
  {
    value: "70000",
    label: "$ 70,000",
  },
  {
    value: "80000",
    label: "$ 80,000",
  },
];

const DEDUCTIBLE_OPTIONS = [
  { value: "1000", label: "$ 1,000" },
  { value: "2000", label: "$ 2,000" },
];

export function NonOwnedTrailerSection() {
  const { control, setValue, formState, getValues, unregister, trigger } =
    useFormContext();

  // carriers for Progressive-only deductible
  const carriers = (useWatch({ control, name: "step8.carrier" }) ?? []).map(
    (s) => s ?? ""
  );
  const isProgressive = carriers.includes("Progressive");
  const hasAccelerant = carriers.includes("Accelerant");

  const coverageChecked =
    useWatch({ control, name: "step8.nonOwnedTrailerCoverage" }) === "yes";

  const rawCount = useWatch({ control, name: "step8.nonOwnedTrailersCount" });
  const countNum =
    typeof rawCount === "number"
      ? rawCount
      : rawCount == null || rawCount === ""
      ? undefined
      : Number.isFinite(Number(rawCount))
      ? Number(rawCount)
      : undefined;

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "step8.nonOwnedTrailers",
  });

  React.useEffect(() => {
    (async () => {
      // Compute target length:
      // - unchecked OR empty/invalid OR <= 0 → target = 0
      // - otherwise clamp 1..9
      let target = 0;
      if (coverageChecked) {
        if (Number.isInteger(countNum)) {
          target = countNum <= 0 ? 0 : Math.min(9, countNum);
        }
      }

      const curr = getValues("step8.nonOwnedTrailers") ?? [];

      // Already correct
      if (curr.length === target) {
        // still ensure validation runs when we *have* rows
        if (target > 0) {
          await trigger("step8.nonOwnedTrailers");
        }
        return;
      }

      if (target === 0) {
        // hard clear → unregister and replace
        unregister("step8.nonOwnedTrailers");
        replace([]);
        // array cleared → clear related errors too
        await trigger("step8.nonOwnedTrailers");
        return;
      }

      if (curr.length > target) {
        // shrinking → unregister removed indices then slice
        for (let i = target; i < curr.length; i++) {
          unregister(`step8.nonOwnedTrailers.${i}`);
        }
        replace(curr.slice(0, target));
        // run validation so missing fields show errors
        await trigger("step8.nonOwnedTrailers");
        return;
      }

      // growing → append blanks
      const toAdd = target - curr.length;
      append(
        Array.from({ length: toAdd }).map(() => ({
          trailerType: undefined,
          coverageAmount: undefined,
          deductible: undefined,
        }))
      );
      // run validation so required errors appear immediately
      await trigger("step8.nonOwnedTrailers");
    })();
  }, [
    coverageChecked,
    countNum,
    getValues,
    unregister,
    append,
    replace,
    trigger,
  ]);

  return (
    <div className="space-y-3 my-1 rounded-lg border p-4">
      <div className="flex items-center gap-3">
        <Controller
          control={control}
          name="step8.nonOwnedTrailerCoverage"
          render={({ field }) => (
            <Checkbox
              id="non-owned-trailer-coverage"
              checked={field.value === "yes"}
              onCheckedChange={(v) => {
                field.onChange(v ? "yes" : "na");
                if (!v) {
                  setValue("step8.nonOwnedTrailersCount", undefined, {
                    shouldValidate: true,
                  });
                  setValue("step8.nonOwnedTrailers", [], {
                    shouldValidate: true,
                  });
                }
              }}
            />
          )}
        />
        <Label htmlFor="non-owned-trailer-coverage" className="cursor-pointer">
          Does the Insured Require Non-Owned Trailer Coverage?
        </Label>
      </div>

      {/* Count */}
      {coverageChecked && (
        <div className="max-w-xs space-y-1">
          <Label>Number of Non-Owned Trailers (1-9)</Label>
          <Controller
            control={control}
            name="step8.nonOwnedTrailersCount"
            render={({ field, fieldState }) => (
              <>
                <Input
                  type="number"
                  min={1}
                  max={9}
                  placeholder="1"
                  // always render a string, allow "" when empty
                  value={field.value === undefined ? "" : String(field.value)}
                  onChange={(e) => {
                    // IMPORTANT: pass the raw string, not a Number
                    // "" stays "", so the input can be cleared
                    field.onChange(e.target.value);
                  }}
                />
                {fieldState.error && (
                  <p className="text-xs text-red-600 mt-1">
                    {fieldState.error.message ??
                      "Only numbers 1-9 will be accepted"}
                  </p>
                )}
              </>
            )}
          />
        </div>
      )}

      {/* Per-trailer blocks */}
      {coverageChecked && typeof countNum === "number" && countNum >= 1 && (
        <div className="space-y-4">
          {Array.from({ length: Math.min(countNum, 9) }).map((_, idx) => (
            <div
              key={
                _
                  ? `${_?.trailerType}-${_?.coverageAmount}-${_?.deductible}`
                  : idx
              }
              className="rounded-lg border p-4 space-y-3"
            >
              <div className="font-medium">Trailer {idx + 1}:</div>

              {/* Type */}
              <div className="space-y-1">
                <Label>Type of Trailer</Label>
                <Controller
                  control={control}
                  name={`step8.nonOwnedTrailers.${idx}.trailerType`}
                  render={({ field, fieldState }) => (
                    <>
                      <Select
                        value={field.value ?? ""}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger
                          className="w-full h-10"
                          key={`${
                            _
                              ? `${_?.trailerType}-${_?.coverageAmount}-${_?.deductible}`
                              : idx
                          }-type`}
                        >
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60 overflow-y-auto">
                          {TRAILER_TYPES.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {fieldState.error && (
                        <p className="text-xs text-red-600 mt-1">
                          {fieldState.error.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>

              {/* Coverage Amount */}
              <div className="space-y-1">
                <Label>Amount of Non-Owned Trailer Coverage</Label>
                <Controller
                  control={control}
                  name={`step8.nonOwnedTrailers.${idx}.coverageAmount`}
                  render={({ field, fieldState }) => (
                    <>
                      <Select
                        value={field.value ?? ""}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger
                          className="w-full h-10"
                          key={`${
                            _
                              ? `${_?.trailerType}-${_?.coverageAmount}-${_?.deductible}`
                              : idx
                          }-coverageAmount`}
                        >
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60 overflow-y-auto">
                          {COVERAGE_AMOUNTS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {fieldState.error && (
                        <p className="text-xs text-red-600 mt-1">
                          {fieldState.error.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>

              {/* Progressive-only: Deductible */}
              {(isProgressive || hasAccelerant) && (
                <div className="space-y-1">
                  <Label>Non-Owned Trailer Deductible</Label>
                  <Controller
                    control={control}
                    name={`step8.nonOwnedTrailers.${idx}.deductible`}
                    render={({ field, fieldState }) => (
                      <>
                        <Select
                          value={field.value ?? ""}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger className="w-full h-10">
                            <SelectValue
                              placeholder="Select"
                              key={`${
                                _
                                  ? `${_?.trailerType}-${_?.coverageAmount}-${_?.deductible}`
                                  : idx
                              }-deductible`}
                            />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto">
                            {DEDUCTIBLE_OPTIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {fieldState.error && (
                          <p className="text-xs text-red-600 mt-1">
                            {fieldState.error.message}
                          </p>
                        )}
                      </>
                    )}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
